import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { Breadcrumbs, BreadcrumbItem } from '../components/ui/Breadcrumbs';
import { LoadingState } from '../components/ui/LoadingState';
import { EmptyState } from '../components/ui/EmptyState';
import type { Analytics } from '../types';
import {
  BarChart3,
  Activity,
  CheckCircle2,
  AlertTriangle,
  Timer,
  DollarSign,
} from 'lucide-react';

export const AnalyticsPage: React.FC = () => {
  const client = useAuthStore((state) => state.client);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('7d');

  const { data: analytics, isLoading } = useQuery({
    queryKey: ['analytics', client?.id, timeRange],
    queryFn: async () => {
      const endDate = new Date();
      const startDate = new Date();

      switch (timeRange) {
        case '7d':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(startDate.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(startDate.getDate() - 90);
          break;
      }

      const response = await api.get<Analytics>(`/analytics/clients/${client?.id}`, {
        params: {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
        },
      });
      return response.data;
    },
    enabled: !!client?.id,
  });

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const successRate =
    analytics && analytics.total_requests > 0
      ? ((analytics.successful_requests / analytics.total_requests) * 100).toFixed(1)
      : '0.0';

  const errorRate =
    analytics && analytics.total_requests > 0
      ? ((analytics.failed_requests / analytics.total_requests) * 100).toFixed(1)
      : '0.0';

  return (
    <div className="container-custom py-8 animate-fade-in">
      {/* Breadcrumbs */}
      <Breadcrumbs className="mb-6">
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem>Analytics</BreadcrumbItem>
      </Breadcrumbs>

      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
            <BarChart3 className="w-8 h-8 text-primary-500" />
            <span>Analytics & Métricas</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm sm:text-base">
            Monitore o consumo, latência e taxa de sucesso das suas chamadas de API
          </p>
        </div>

        {/* Segmented Period Controls */}
        <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-semibold shadow-2xs">
          {(
            [
              { id: '7d', label: '7 Dias' },
              { id: '30d', label: '30 Dias' },
              { id: '90d', label: '90 Dias' },
            ] as const
          ).map((period) => (
            <button
              key={period.id}
              type="button"
              onClick={() => setTimeRange(period.id)}
              className={`px-3.5 py-1.5 rounded-lg whitespace-nowrap transition-all ${
                timeRange === period.id
                  ? 'bg-white dark:bg-slate-700 text-primary-600 dark:text-primary-400 shadow-2xs font-bold'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <LoadingState type="card" count={4} />
      ) : analytics ? (
        <div className="space-y-8">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {/* Total Requests */}
            <div className="card-modern p-6 relative overflow-hidden group hover:border-primary-500/50 transition-all">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Total de Requisições
                </span>
                <div className="p-2 rounded-xl bg-primary-50 dark:bg-primary-950/40 text-primary-600 dark:text-primary-400">
                  <Activity className="w-5 h-5" />
                </div>
              </div>
              <p className="text-3xl font-bold text-slate-900 dark:text-white">
                {formatNumber(analytics.total_requests)}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                Volume de chamadas no período
              </p>
            </div>

            {/* Successful Requests */}
            <div className="card-modern p-6 relative overflow-hidden group hover:border-emerald-500/50 transition-all">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Bem-Sucedidas
                </span>
                <div className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
              </div>
              <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                {formatNumber(analytics.successful_requests)}
              </p>
              <div className="flex items-center gap-1.5 mt-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                  {successRate}% sucesso
                </span>
              </div>
            </div>

            {/* Failed Requests */}
            <div className="card-modern p-6 relative overflow-hidden group hover:border-rose-500/50 transition-all">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Falhas & Erros
                </span>
                <div className="p-2 rounded-xl bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400">
                  <AlertTriangle className="w-5 h-5" />
                </div>
              </div>
              <p className="text-3xl font-bold text-rose-600 dark:text-rose-400">
                {formatNumber(analytics.failed_requests)}
              </p>
              <div className="flex items-center gap-1.5 mt-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300">
                  {errorRate}% erro
                </span>
              </div>
            </div>

            {/* Average Latency */}
            <div className="card-modern p-6 relative overflow-hidden group hover:border-amber-500/50 transition-all">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Tempo de Resposta
                </span>
                <div className="p-2 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400">
                  <Timer className="w-5 h-5" />
                </div>
              </div>
              <p className="text-3xl font-bold text-slate-900 dark:text-white">
                {analytics.average_response_time?.toFixed(0) ?? '0'}
                <span className="text-lg font-medium text-slate-400 ml-1">ms</span>
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                Latência média de ponta a ponta
              </p>
            </div>
          </div>

          {/* Revenue & Total Spent Card */}
          <div className="card-modern p-6 bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3.5 rounded-2xl bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400">
                <DollarSign className="w-7 h-7" />
              </div>
              <div>
                <p className="text-xs uppercase font-semibold text-slate-400 dark:text-slate-500 tracking-wider">
                  Gasto Total no Período
                </p>
                <p className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white mt-1">
                  ${analytics.total_revenue?.toFixed(2) ?? '0.00'}
                </p>
              </div>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 max-w-xs">
              Valor consolidado com base na tarifação por chamada ou subscrições ativas das suas
              aplicações.
            </div>
          </div>

          {/* Requests by Day Chart */}
          <div className="card-modern p-6 sm:p-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                  Requisições ao Longo do Tempo
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Distribuição diária das requisições registradas no gateway
                </p>
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                {analytics.requests_by_day.length} dias registrados
              </span>
            </div>

            <div className="h-64 flex items-end gap-2 pt-6 border-b border-slate-200 dark:border-slate-800">
              {analytics.requests_by_day.map((day, index) => {
                const maxCount = Math.max(...analytics.requests_by_day.map((d) => d.count), 1);
                const height = (day.count / maxCount) * 100;

                return (
                  <div key={index} className="flex-1 flex flex-col items-center h-full justify-end group">
                    <div
                      className="w-full rounded-t-lg bg-gradient-to-t from-primary-600 to-primary-500 group-hover:from-primary-500 group-hover:to-primary-400 transition-all cursor-pointer relative"
                      style={{
                        height: `${Math.max(height, 4)}%`,
                      }}
                      title={`${formatNumber(day.count)} requisições`}
                    >
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-2xs px-2 py-1 rounded-md whitespace-nowrap pointer-events-none z-10 shadow-lg">
                        {formatNumber(day.count)} reqs
                      </div>
                    </div>
                    <p className="text-2xs text-slate-500 dark:text-slate-400 mt-2 truncate w-full text-center">
                      {new Date(day.date).toLocaleDateString('pt-BR', {
                        month: 'numeric',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Requests by Endpoint */}
          <div className="card-modern p-6 sm:p-8">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
              Top Endpoints Mais Acessados
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-6">
              Rotas com maior volume de tráfego e distribuição proporcional
            </p>

            {Object.keys(analytics.requests_by_endpoint).length > 0 ? (
              <div className="space-y-5">
                {Object.entries(analytics.requests_by_endpoint)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 10)
                  .map(([endpoint, count], idx) => {
                    const percentage =
                      analytics.total_requests > 0
                        ? (count / analytics.total_requests) * 100
                        : 0;

                    return (
                      <div key={endpoint} className="group">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="w-5 text-xs font-bold text-slate-400 dark:text-slate-500">
                              #{idx + 1}
                            </span>
                            <span className="font-mono text-xs sm:text-sm font-medium text-slate-800 dark:text-slate-200 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                              {endpoint}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                              {percentage.toFixed(1)}%
                            </span>
                            <span className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white">
                              {formatNumber(count)} reqs
                            </span>
                          </div>
                        </div>
                        <div className="w-full rounded-full h-2.5 bg-slate-100 dark:bg-slate-800 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-primary-600 to-indigo-500 transition-all duration-500"
                            style={{
                              width: `${Math.min(Math.max(percentage, 2), 100)}%`,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <p className="text-center text-slate-500 dark:text-slate-400 py-8 text-sm">
                Nenhum dado de endpoint disponível.
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="card-modern p-12 mt-8 text-center">
          <EmptyState
            title="Nenhum dado de analytics disponível"
            description="Comece a usar as APIs para visualizar suas métricas de consumo e desempenho aqui."
          />
        </div>
      )}
    </div>
  );
};
